import * as React from 'react';
import {connect} from 'react-redux';
import * as VehiclesModule from 'Modules/VehiclesModule';
import * as MapModule from 'Modules/MapModule';
import * as ErrorActions from 'Redux/Actions/ErrorActions';
import {Map, TileLayer} from 'react-leaflet';
import {
    mapBounds,
    minZoom,
    maxZoom,
    mapOptions,
    osmURL,
    generateIconHelperData
} from 'components/Map/MapHelpers/MapHelpers';
import {ReportBlock} from 'components/Raports/ReportsComposites/CarMainReport';
import {withTranslation} from 'react-i18next';
import i18next from 'i18next';
import {Marker} from 'react-leaflet';
import * as L from 'leaflet';
import moment from 'moment';
import {Cell} from 'HelperFunctions/Lists';
import MediaQuery from 'react-responsive';
import {compose} from "redux";

type Props = {
    match?: any;
    t?: i18next.TFunction;
};

type ConnectedDispatch = {
    getCarDetails: (carId: number) => void;
    uploadCarImage: (carId: number, file: Blob) => void;
    getCarImages: (carId: number) => void;
    updateCarLatestDataframe: (message: VehiclesModule.Types.CarDataframeDto, carId: number) => void;
    setSmallMapCenter: (center: MapModule.Types.MapCenter) => void;
    setSSEResponseError: (err: any) => void;
};

type ConnectedProps = {
    singleCarViewData: VehiclesModule.Types.CarResponse;
    mapStore: MapModule.Types.MapStore;
};
type State = {
    //
};

class CarDiagnostic extends React.Component<Props & ConnectedProps & ConnectedDispatch, State> {
    private sse: EventSource;

    constructor(props: Props & ConnectedProps & ConnectedDispatch) {
        super(props);
        this.manageSse = this.manageSse.bind(this);
    }

    private manageSse() {
        const {carId} = this.props.match.params;
        try {
            this.sse = VehiclesModule.Connector.openWSSWithCarDiagnostic(carId);
            this.sse.addEventListener('dataframe', (message: MessageEvent) => {
                if (message.data) {
                    this.props.updateCarLatestDataframe(JSON.parse(message.data), carId);
                }
            });
            this.sse.onerror = (err: MessageEvent) => {
                this.props.setSSEResponseError(err);
            };
        } catch (err) {
            this.props.setSSEResponseError(err);
        }
    }

    public async componentDidMount() {
        const {carId} = this.props.match.params;
        const {getCarDetails} = this.props;
        this.manageSse();
        await getCarDetails(carId);
    }

    public componentWillUnmount() {
        this.sse.close();
    }

    public componentDidUpdate() {
        const {singleCarViewData, mapStore, setSmallMapCenter} = this.props;
        if (singleCarViewData && singleCarViewData.car.latestDataframe && singleCarViewData.car.latestDataframe.latitude !== mapStore.smallMap.center.lat) {
            setSmallMapCenter({
                lat: singleCarViewData.car.latestDataframe.latitude,
                lng: singleCarViewData.car.latestDataframe.longitude
            });
        }
    }

    public prepareCarIcon() {
        const element = this.props.singleCarViewData.car;
        const iconHelper: VehiclesModule.Types.IconHelper = generateIconHelperData(element.carCategory, element.latestDataframe.speedLimitExceedance);
        const html = `<p class="vehicle-icon__title">${element.name || element.vin} </br><span>${element.deviceId}</span></p> 
            <img style="transform: rotate(${element.latestDataframe.heading ? `${element.latestDataframe.heading}deg` : '0deg'})" src="${iconHelper.imageUrl}">`;

        return L.divIcon({
            iconUrl: iconHelper.imageUrl,
            iconSize: iconHelper.imageSize,
            html,
            className: `vehicle-icon vehicle-icon__${element.latestDataframe.speedLimitExceedance.toLowerCase()}`
        });
    }

    public render() {
        const {mapStore, t} = this.props;
        const {singleCarViewData} = this.props;
        let position: MapModule.Types.MapCenter;
        if (singleCarViewData && singleCarViewData.car.latestDataframe) {
            position = {
                lat: singleCarViewData.car.latestDataframe.latitude,
                lng: singleCarViewData.car.latestDataframe.longitude
            };
        }

        return (
            <div className="main main__content main__content--no-padding">
                <MediaQuery query="(max-width: 1279px)">
                    <div className="main__inner-right--flex-2l clear-padding">
                        <Map center={mapStore.smallMap.center} zoom={12} options={mapOptions} maxZoom={maxZoom}
                             minZoom={minZoom} zoomControl={false} maxBounds={mapBounds}>
                            <TileLayer url={osmURL}/>
                            {singleCarViewData && singleCarViewData.car.latestDataframe &&
                            <Marker position={position} icon={this.prepareCarIcon()}/>}
                        </Map>
                    </div>
                </MediaQuery>
                <div className="main__inner-left">
                    {singleCarViewData && (
                        <>
                            <h3 className="main__title">{t('carDiagnostic')}</h3>
                            <div className="report-list">
                                <ReportBlock title={t('carId')} value={singleCarViewData.car.id}/>
                                <ReportBlock title={t('deviceId')} value={singleCarViewData.car.deviceId}/>
                                <ReportBlock title={t('carCategory')} value={t(singleCarViewData.car.carCategory)}/>
                                <ReportBlock title={t('licensePlate')} value={singleCarViewData.car.licensePlate}/>
                                <ReportBlock title={t('name')} value={singleCarViewData.car.name}/>
                                <ReportBlock title={t('vin')} value={singleCarViewData.car.vin}/>
                                <ReportBlock title={t('MSISDN')} value={singleCarViewData.car.msisdn}/>
                                <ReportBlock title={t('forms:status')}
                                             value={singleCarViewData.car.turnedOn ? t('isTurnedOn') : t('isTurnedOff')}/>
                            </div>
                            {singleCarViewData.car.turnOffDate && <>
                                <h3 className="main__title">{t('turnOff')}</h3>
                                <div className="report-list">
                                    <ReportBlock title={t('turnOffWhen')} value={singleCarViewData.car.turnOffDate}/>
                                    <ReportBlock title={t('forms:turnOffReason')}
                                                 value={t(`dictionary:${singleCarViewData.car.turnOffReason}`)}/>
                                    <ReportBlock title={t('forms:turnOffNote')}
                                                 value={singleCarViewData.car.turnOffNote}/>
                                </div>
                            </>}
                            <h3 className="main__title">{t('latestDataFrame')}</h3>
                            {singleCarViewData.car.latestDataframe && (
                                <div className="report-list">
                                    <ReportBlock title={t('acceleration')} value={Cell.Acceleration({
                                        value: singleCarViewData.car.latestDataframe.acceleration,
                                        toFixedNo: 2
                                    })}/>
                                    <ReportBlock title={t('accelerationX')} value={Cell.Acceleration({
                                        value: singleCarViewData.car.latestDataframe.accelerationX,
                                        toFixedNo: 2
                                    })}/>
                                    <ReportBlock title={t('accelerationY')} value={Cell.Acceleration({
                                        value: singleCarViewData.car.latestDataframe.accelerationY,
                                        toFixedNo: 2
                                    })}/>
                                    <ReportBlock title={t('accelerationZ')} value={Cell.Acceleration({
                                        value: singleCarViewData.car.latestDataframe.accelerationZ,
                                        toFixedNo: 2
                                    })}/>
                                    <ReportBlock title={t('address')}
                                                 value={singleCarViewData.car.latestDataframe.address.addressDisplayName}/>
                                    <ReportBlock title={t('dateTime')}
                                                 value={moment(singleCarViewData.car.latestDataframe.dateTime).format('HH:mm:ss, DD.MM.YYYY')}/>
                                    <ReportBlock title={t('latitude')} value={Cell.Number({
                                        value: singleCarViewData.car.latestDataframe.latitude,
                                        toFixedNo: 4
                                    })}/>
                                    <ReportBlock title={t('longitude')} value={Cell.Number({
                                        value: singleCarViewData.car.latestDataframe.longitude,
                                        toFixedNo: 4
                                    })}/>
                                    <ReportBlock title={t('heading')}
                                                 value={Cell.Heading({value: singleCarViewData.car.latestDataframe.heading})}/>
                                    <ReportBlock title={t('gsmSignal')}
                                                 value={singleCarViewData.car.latestDataframe.gsmSignal ? `${Math.round(singleCarViewData.car.latestDataframe.gsmSignal)} %` : '-'}/>
                                    <ReportBlock
                                        title={t('mainPowerSupplyVoltage')}
                                        value={singleCarViewData.car.latestDataframe.mainPowerSupplyVoltage ? `${singleCarViewData.car.latestDataframe.mainPowerSupplyVoltage} V` : '-'}
                                    />
                                    <ReportBlock
                                        title={t('backupPowerSupplyVoltage')}
                                        value={singleCarViewData.car.latestDataframe.backupPowerSupplyVoltage ? `${singleCarViewData.car.latestDataframe.backupPowerSupplyVoltage} V` : '-'}
                                    />

                                    <ReportBlock
                                        title={t('mileageGps')}
                                        value={singleCarViewData.car.latestDataframe.mileageGps ? `${singleCarViewData.car.latestDataframe.mileageGps} km` : '0 km'}
                                    />
                                    <ReportBlock title={t('satellite')}
                                                 value={singleCarViewData.car.latestDataframe.satellite}/>
                                    <ReportBlock
                                        title={t('speedGps')}
                                        value={<span
                                            className={singleCarViewData.car.latestDataframe.speedLimitExceedance}>{singleCarViewData.car.latestDataframe.speedGps} km/h</span>}
                                    />
                                    <ReportBlock title={t('speedLimitKmh')}
                                                 value={singleCarViewData.car.latestDataframe.speedLimitKmh}/>
                                    <ReportBlock title={t('turnedOn')}
                                                 value={singleCarViewData.car.latestDataframe.turnedOn ? t('yes') : t('no')}/>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <MediaQuery query="(min-width: 1280px)">
                    <div className="main__inner-right--flex-2 clear-padding">
                        <Map center={mapStore.smallMap.center} zoom={12} options={mapOptions} maxZoom={maxZoom}
                             minZoom={minZoom} zoomControl={false} maxBounds={mapBounds}>
                            <TileLayer url={osmURL}/>
                            {singleCarViewData && singleCarViewData.car.latestDataframe &&
                            <Marker position={position} icon={this.prepareCarIcon()}/>}
                        </Map>
                    </div>
                </MediaQuery>
            </div>
        );
    }
}

function mapStateToProps(state: any): ConnectedProps {
    return {
        singleCarViewData: state.vehiclesStore.singleCarViewData,
        mapStore: state.mapStore
    };
}

const mapDispatchToProps: ConnectedDispatch = {
    getCarDetails: (carId: number) => VehiclesModule.Actions.getSingleCarDetails(carId),
    uploadCarImage: (carId: number, file: Blob) => VehiclesModule.Actions.uploadCarImage(carId, file),
    getCarImages: (carId: number) => VehiclesModule.Actions.getCarImages(carId),
    updateCarLatestDataframe: (message: VehiclesModule.Types.CarDataframeDto, carId: number) => VehiclesModule.Actions.updateCarLatestDataframe(message, carId),
    setSmallMapCenter: (center: MapModule.Types.MapCenter) => MapModule.Actions.smallMapsetMapCenter(center),
    setSSEResponseError: (err: any) => ErrorActions.setSSEResponseError(err)
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withTranslation(['carDetails', 'forms', 'dictionary'])
)(CarDiagnostic) as React.ComponentClass<any>



// WEBPACK FOOTER //
// ./src/components/CarsManagement/CarDiagnostic.tsx